import { useLayoutEffect, useRef, useState } from "react";
import type { Block, PresetRef } from "../contract/types";
import { Icon } from "../components/Icon";
import { BlockRenderer } from "../blocks/BlockRenderer";
import type { BlockEventSink } from "../blocks/types";
import { domLengthToRem, elementSizeToRem } from "../lib/rem";
import { visibleAnswerText } from "../lib/answer";
import { formatTime } from "../lib/format";
import type { ConnectionState } from "../stream/useRun";
import type { Turn } from "../stream/reducer";

/**
 * Centro del layout: el stream arriba, el composer abajo.
 *
 * Texto y bloques van intercalados en una sola columna —el orden de emisión es
 * el orden de lectura— y los bloques `half` consecutivos se emparejan en una
 * fila, que es la convención del envelope (`width: half` solo tiene sentido en
 * pareja).
 *
 * La llegada de texto o bloques conserva la posición de lectura.
 * Un indicador avisa cuando queda contenido por debajo del viewport.
 */

const BELOW_VIEWPORT_THRESHOLD_REM = 0.5;

export interface StreamViewProps {
  turns: Turn[];
  isStreaming: boolean;
  connection: ConnectionState;
  error: string | null;
  emit: BlockEventSink;
  interactive: boolean;
  onSend: (content: string) => void;
  onStop: () => void;
  onOpenPreset: (preset: PresetRef) => void;
  draft: string;
  onDraftChange: (value: string) => void;
  topic: string | null;
  onClearTopic: () => void;
}

const SUGGESTIONS = [
  "¿Cómo me evalúan en el curso?",
  "Explícame el WACC con un ejemplo",
  "Quiero practicar el VAN de un proyecto",
];

export function StreamView({
  turns,
  isStreaming,
  connection,
  error,
  emit,
  interactive,
  onSend,
  onStop,
  onOpenPreset,
  draft,
  onDraftChange,
  topic,
  onClearTopic,
}: StreamViewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showJump, setShowJump] = useState(false);
  const lastTurn = turns[turns.length - 1];

  function updateOverflow() {
    const node = scrollRef.current;
    if (!node) return;
    const remaining = domLengthToRem(node.scrollHeight - node.scrollTop - node.clientHeight);
    setShowJump(remaining > BELOW_VIEWPORT_THRESHOLD_REM);
  }

  useLayoutEffect(() => {
    updateOverflow();
    const node = scrollRef.current;
    const content = node?.firstElementChild;
    if (!node || !content) return;
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(node);
    observer.observe(content);
    return () => observer.disconnect();
  }, [turns, isStreaming]);

  function scrollDown() {
    const node = scrollRef.current;
    if (!node) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    node.scrollBy({ top: node.clientHeight * 0.8, behavior: reducedMotion ? "instant" : "smooth" });
  }

  return (
    <div className="stream">
      <div className="stream__viewport">
        <div className="stream__scroll" ref={scrollRef} onScroll={updateOverflow}>
          <div className="stream__inner">
            {turns.length === 0 ? (
              <Welcome onPick={onSend} disabled={!interactive || isStreaming} />
            ) : (
              turns.map((turn) => (
                <TurnView
                  key={turn.id}
                  turn={turn}
                  emit={emit}
                  interactive={interactive}
                  onOpenPreset={onOpenPreset}
                  isLast={turn.id === lastTurn?.id}
                />
              ))
            )}

            {error ? (
              <div className="stream__error" role="alert">
                <Icon name="alert" size="1rem" />
                <span>{error}</span>
              </div>
            ) : null}
          </div>

        </div>
        {showJump ? (
          <button type="button" className="stream__jump u-button u-button--small" onClick={scrollDown} aria-label="Bajar para leer más contenido">
            <Icon name="arrow-down" size="0.75rem" />
            Más contenido abajo
          </button>
        ) : null}
      </div>

      <Composer
        draft={draft}
        onDraftChange={onDraftChange}
        topic={topic}
        onClearTopic={onClearTopic}
        interactive={interactive}
        onSend={onSend}
        onStop={onStop}
        isStreaming={isStreaming}
        connection={connection}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Turno                                                                      */
/* -------------------------------------------------------------------------- */

function TurnView({
  turn,
  emit,
  interactive,
  onOpenPreset,
  isLast,
}: {
  turn: Turn;
  emit: BlockEventSink;
  interactive: boolean;
  onOpenPreset: (preset: PresetRef) => void;
  isLast: boolean;
}) {

  if (turn.role === "user") {
    return (
      <article className="turn turn--user">
        <div className="turn__bubble">
          {turn.text.startsWith("@") && turn.text.includes("\n") ? (
            <><span className="topic-tag">{turn.text.split("\n")[0]}</span><br />{turn.text.slice(turn.text.indexOf("\n") + 1)}</>
          ) : turn.text}
        </div>
        <span className="turn__time">{formatTime(turn.createdAt)}</span>
      </article>
    );
  }

  const streaming = turn.status === "streaming" || turn.status === "connecting";
  const answerText = visibleAnswerText(turn.text);
  const empty = answerText === "" && turn.blocks.length === 0;

  return (
    <article className="turn turn--assistant" data-status={turn.status}>
      <header className="turn__head">
        <span className="turn__author">
          <Icon name="sigma" size="0.875rem" />
          Asistente
        </span>
      </header>

      {answerText !== "" ? (
        <div className="turn__text">
          <BlockRenderer
            block={proseBlock(turn)}
            emit={emit}
            interactive={interactive}
            onNavigate={(target) => {
              if (target.kind === "preset") onOpenPreset(target.preset);
            }}
          />
        </div>
      ) : null}

      {turn.blocks.length > 0 ? (
        <div className="turn__blocks">
          <BlockGroups
            blocks={turn.blocks}
            emit={emit}
            interactive={interactive}
            onOpenPreset={onOpenPreset}
          />
        </div>
      ) : null}

      {streaming ? <p className="turn__process" role="status">Preparando respuesta…</p> : null}

      {empty && turn.status === "done" ? (
        <p className="turn__empty">El turno no produjo contenido.</p>
      ) : null}

      {turn.error ? (
        <div className="turn__error" role="alert">
          <Icon name="alert" size="1rem" />
          <span>{turn.error.message}</span>
        </div>
      ) : null}

      {!streaming && isLast && turn.status !== "error" ? (
        <span className="turn__time">{formatTime(turn.createdAt)}</span>
      ) : null}
    </article>
  );
}

/** El texto en curso se pinta con el átomo `prose`, como cualquier respuesta. */
function proseBlock(turn: Turn): Block {
  return {
    id: `${turn.id}-text`,
    type: "prose",
    markdown: visibleAnswerText(turn.text),
  } as Block;
}

/**
 * Agrupa los bloques consecutivos de ancho `half` en filas de dos. Un `half`
 * suelto se queda a media anchura y descolgado: es la señal de que el envelope
 * prometió una pareja que no llegó.
 */
function BlockGroups({
  blocks,
  emit,
  interactive,
  onOpenPreset,
}: {
  blocks: Block[];
  emit: BlockEventSink;
  interactive: boolean;
  onOpenPreset: (preset: PresetRef) => void;
}) {
  const rows: Block[][] = [];
  let pending: Block[] = [];

  const orderedBlocks = [...blocks.filter((block) => block.type !== "source_list"), ...blocks.filter((block) => block.type === "source_list")];
  for (const block of orderedBlocks) {
    if (block.meta?.width === "half") {
      pending.push(block);
      if (pending.length === 2) {
        rows.push(pending);
        pending = [];
      }
    } else {
      if (pending.length > 0) {
        rows.push(pending);
        pending = [];
      }
      rows.push([block]);
    }
  }
  if (pending.length > 0) rows.push(pending);

  return (
    <>
      {rows.map((row, index) => (
        <div
          key={row.map((block) => block.id).join("|") || index}
          className={row.length === 2 ? "turn__row turn__row--pair" : "turn__row"}
        >
          {row.map((block) => (
            <BlockRenderer
              key={block.id}
              block={block}
              emit={emit}
              interactive={interactive}
              onNavigate={(target) => {
                if (target.kind === "preset") onOpenPreset(target.preset);
              }}
            />
          ))}
        </div>
      ))}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Composer                                                                   */
/* -------------------------------------------------------------------------- */

function Composer({
  draft,
  onDraftChange,
  topic,
  onClearTopic,
  onSend,
  onStop,
  isStreaming,
  connection,
  interactive,
}: {
  interactive: boolean;
  draft: string;
  onDraftChange: (value: string) => void;
  topic: string | null;
  onClearTopic: () => void;
  onSend: (content: string) => void;
  onStop: () => void;
  isStreaming: boolean;
  connection: ConnectionState;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const node = textareaRef.current;
    if (!node) return;
    // Auto-alto: se mide el contenido y se devuelve en rem antes de aplicarlo.
    node.style.height = "auto";
    const measured = elementSizeToRem(node, "scrollHeight");
    const next = Math.min(Math.max(measured, 3), 14);
    node.style.height = `${next}rem`;
  }, [draft]);

  useLayoutEffect(() => {
    if (topic) textareaRef.current?.focus();
  }, [topic]);

  function submit() {
    const value = draft.trim();
    if (value === "" || isStreaming || !interactive) return;
    onSend(value);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  }

  const busy = connection === "connecting" || connection === "retrying";

  return (
    <div className="composer">
      <div className="composer__box">
        <div className="composer__field">
        {topic ? <button type="button" className="topic-tag" onClick={onClearTopic} aria-label={`Quitar tema ${topic}`}>@{topic} <span aria-hidden="true">×</span></button> : null}
        <textarea
          ref={textareaRef}
          className="composer__input"
          value={draft}
          placeholder="Pregunta sobre el curso, pide un cálculo o un plan de estudio…"
          rows={1}
          onChange={(event) => onDraftChange(event.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="Mensaje para el asistente"
        />
        </div>
        <div className="composer__actions">
          {isStreaming ? (
            <button type="button" className="u-button" onClick={onStop}>
              <Icon name="stop" size="0.75rem" />
              Detener
            </button>
          ) : (
            <button
              type="button"
              className="u-button u-button--primary composer__send"
              disabled={draft.trim() === "" || busy || !interactive}
              onClick={submit}
            >
              <Icon name="arrow-up" size="0.875rem" />
              Enviar
            </button>
          )}
        </div>
      </div>
      <div className="composer__footer">
        <span className="composer__hint">
          <kbd>Enter</kbd> envía · <kbd>Shift</kbd> + <kbd>Enter</kbd> salto de línea
        </span>
        {(connection === "retrying" || connection === "connecting") && isStreaming ? (
          <span className="composer__connection">reconectando…</span>
        ) : null}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Estado vacío                                                               */
/* -------------------------------------------------------------------------- */

function Welcome({ onPick, disabled }: { onPick: (text: string) => void; disabled: boolean }) {
  return (
    <div className="welcome">
      <div className="welcome__mark" aria-hidden="true">
        <Icon name="sigma" size="1.75rem" />
      </div>
      <h1 className="welcome__title">¿Qué trabajamos hoy?</h1>
      <p className="welcome__lead">
        Pregunta sobre el curso, repasa conceptos o practica con ejercicios.
        Selecciona un tema del menú para enfocar tu consulta.
      </p>
      <ul className="welcome__suggestions">
        {SUGGESTIONS.map((suggestion) => (
          <li key={suggestion}>
            <button type="button" className="welcome__suggestion" disabled={disabled} onClick={() => onPick(suggestion)}>
              <Icon name="arrow-right" size="0.75rem" />
              {suggestion}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default StreamView;
